import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiServiceBase } from './api.service.base';
import { UserStateService } from './user-state.service';

import { logger } from '../../helpers/logger';
export interface SecurityOverrideModel {
  userId?: string;
  clientId?: number;
}

export interface ImpersonationState {
  isImpersonating: boolean;
  hasSecurityOverrides: boolean;
  overrideDescription?: string;
}

export interface UserLookupModel {
  id: string;
  email: string;
  globalRoleId?: number;
  client_ID?: number;
  clientName: string;
  isInternal?: boolean;
}

export interface UserLookupGroup {
  groupName: string;
  users: UserLookupModel[];
}

export interface ClientLookupItem {
  id: number;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class ImpersonationService extends ApiServiceBase {
  private userStateService = inject(UserStateService);
  private impersonationStateSubject = new BehaviorSubject<ImpersonationState>({
    isImpersonating: false,
    hasSecurityOverrides: false,
  });
  public impersonationState$ = this.impersonationStateSubject.asObservable();

  startImpersonation(model: SecurityOverrideModel): Observable<void> {
    return this.post<void>('account/impersonate/start', model);
  }

  stopImpersonation(): Observable<void> {
    return this.post<void>('account/impersonate/stop', {});
  }

  getUserLookup(): Observable<UserLookupModel[]> {
    return this.get<UserLookupModel[]>('users/lookup');
  }

  getClientLookup(): Observable<ClientLookupItem[]> {
    return this.get<ClientLookupItem[]>('clients/lookup');
  }

  updateImpersonationState(state: ImpersonationState): void {
    this.impersonationStateSubject.next(state);
  }

  getCurrentImpersonationState(): ImpersonationState {
    return this.impersonationStateSubject.value;
  }

  getImpersonationState(): Observable<ImpersonationState> {
    return this.userStateService.impersonationState$.pipe(
      map(
        state =>
          state || {
            isImpersonating: false,
            hasSecurityOverrides: false,
          }
      )
    );
  }

  getImpersonationDetails(): Observable<{ userId: string | null; clientId: number | null; isImpersonating: boolean }> {
    return this.get<{ userId: string | null; clientId: number | null; isImpersonating: boolean }>(
      'account/impersonation-details'
    );
  }

  loadImpersonationState(): void {
    this.getImpersonationState().subscribe({
      next: state => this.updateImpersonationState(state),
      error: error => {
        logger.error('Failed to load impersonation state:', error);
        this.updateImpersonationState({ isImpersonating: false, hasSecurityOverrides: false });
      },
    });
  }

  // Helper method to group users by client
  groupUsersByClient(users: UserLookupModel[]): UserLookupGroup[] {
    const groups: { [key: string]: UserLookupGroup } = {};
    const noClientKey = 'no-client';

    // Group users by client
    users.forEach(user => {
      const clientKey = user.client_ID?.toString() || noClientKey;
      const groupName = user.client_ID ? user.clientName : 'No Client';

      if (!groups[clientKey]) {
        groups[clientKey] = {
          groupName,
          users: [],
        };
      }
      groups[clientKey].users.push(user);
    });

    // Convert to array and sort
    const sortedGroups = Object.values(groups).sort((a, b) => {
      // Internal client groups come first
      const aHasInternal = a.users.some(u => u.isInternal);
      const bHasInternal = b.users.some(u => u.isInternal);

      if (aHasInternal && !bHasInternal) return -1;
      if (!aHasInternal && bHasInternal) return 1;

      // No client group comes last
      if (a.groupName === 'No Client') return 1;
      if (b.groupName === 'No Client') return -1;

      // Otherwise sort alphabetically
      return a.groupName.localeCompare(b.groupName);
    });

    // Sort users within each group by email
    sortedGroups.forEach(group => {
      group.users.sort((a, b) => a.email.localeCompare(b.email));
    });

    return sortedGroups;
  }
}
